/**
 * F1.6.0 management view. CR-2026-CONTRACT-REVIEW (2026-10-15).
 * One page for the eight signatories: the verdict, scope, each F1.6.0 section's status, the gaps (RFIs and open questions),
 * the decisions sent to them, and who has signed. Read-only; built from GET /api/contract-review/jobs/:id.
 * The review states what the documents say and do not say. Decisions rest with the signatories.
 */
import React from "react";
import { Alert, Box, Card, Chip, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { SECTION_STATUS, REVIEW_STATUS_LABEL, answerText, citationText, fmtLbs, gaps, verdict } from "./contractReviewModel";

import SmartTable from "../../components/SmartTable/SmartTable";
export default function ManagementView({ data }) {
  const v = verdict(data);
  const r = data.review;
  const scope = data.scope || {};
  const decisions = r ? r.answers.filter((a) => a.reviewStatus === "DECISION_REQUIRED") : [];
  const gapList = r ? gaps(r).filter((a) => a.reviewStatus !== "DECISION_REQUIRED") : [];
  return (
    <Box>
      <Alert severity={v.ready ? "success" : "error"} sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Verdict: {v.label}</Typography>
        {v.ready ? (
          <Typography variant="body2">Every required signatory accepted F1.6.0 revision {r.revision}. Release is open for this job.</Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{v.reasons.map((x) => <li key={x}><Typography variant="body2">{x}</Typography></li>)}</Box>
        )}
      </Alert>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[["Marks", scope.marks], ["Pieces", scope.pieces], ["Recorded weight", fmtLbs(scope.weightLbs)],
          ["Loads at 45,000 lb", scope.loadsAt45000 ?? "n/a"]].map(([label, val]) => (
          <Grid item xs={6} md={3} key={label}>
            <Card sx={{ p: 1.5 }}><Typography variant="h5">{val ?? 0}</Typography><Typography variant="caption">{label}</Typography></Card>
          </Grid>
        ))}
      </Grid>
      {scope.marksWithoutWeight > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -1, mb: 2 }}>
          {scope.marksWithoutWeight} mark(s) have no recorded weight, so no total is shown.
        </Typography>
      )}

      {r && (
        <>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>F1.6.0 section status (revision {r.revision})</Typography>
            <SmartTable tableId="contract-sections" lockedColumns={["Section", "Status", "Decision"]} size="small">
              <TableHead><TableRow>
                <TableCell>Section</TableCell><TableCell>Status</TableCell><TableCell align="right">Stated</TableCell>
                <TableCell align="right">N/A</TableCell><TableCell align="right">Not stated (RFI)</TableCell>
                <TableCell align="right">Decision</TableCell><TableCell align="right">Open</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {r.sections.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell>{s.title}</TableCell>
                    <TableCell><Chip size="small" label={SECTION_STATUS[s.status].label} color={SECTION_STATUS[s.status].color} /></TableCell>
                    <TableCell align="right">{s.counts.STATED}</TableCell><TableCell align="right">{s.counts.NA}</TableCell>
                    <TableCell align="right">{s.counts.NOT_STATED_RFI}</TableCell><TableCell align="right">{s.counts.DECISION_REQUIRED}</TableCell>
                    <TableCell align="right">{s.counts.OPEN}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Decisions for the signatories ({decisions.length})</Typography>
            {decisions.length === 0 ? <Typography variant="body2" color="text.secondary">None.</Typography> : (
              <SmartTable tableId="contract-questions" lockedColumns={["Question"]} size="small">
                <TableHead><TableRow><TableCell>Question</TableCell><TableCell>For</TableCell><TableCell>What is to be decided</TableCell></TableRow></TableHead>
                <TableBody>
                  {decisions.map((a) => (
                    <TableRow key={a.key}>
                      <TableCell>{a.question}</TableCell>
                      <TableCell>{(r.slots.find((s) => s.key === a.decisionSlot) || {}).label || (a.state === "CONFLICT" ? "Reviewer" : "")}</TableCell>
                      <TableCell>{a.state === "CONFLICT" ? answerText(a) : a.decisionNote}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            )}
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Gaps: not stated in the documents ({gapList.length})</Typography>
            {gapList.length === 0 ? <Typography variant="body2" color="text.secondary">None.</Typography> : (
              <SmartTable tableId="contract-rfis" lockedColumns={["Question", "Status", "RFI"]} size="small">
                <TableHead><TableRow><TableCell>Question</TableCell><TableCell>Status</TableCell><TableCell>RFI</TableCell></TableRow></TableHead>
                <TableBody>
                  {gapList.map((a) => (
                    <TableRow key={a.key}>
                      <TableCell>{a.question}</TableCell>
                      <TableCell>{REVIEW_STATUS_LABEL[a.reviewStatus]}</TableCell>
                      <TableCell>{a.rfi ? `${a.rfi.number} (${String(a.rfi.status).toLowerCase()})` : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            )}
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Key answers</Typography>
            <Table size="small">
              <TableBody>
                {["GEN_CUSTOMER", "MAT_MAIN_GRADE", "MAT_FOREIGN", "MAT_TRACE", "WLD_CODE", "WLD_NDT", "FAS_FIELD_BOLT", "MAT_ANCHOR_GRADE", "COAT_TYPE", "SPC_PAYMENT", "SPC_LDS"]
                  .map((k) => r.answers.find((a) => a.key === k)).filter(Boolean).map((a) => (
                    <TableRow key={a.key}>
                      <TableCell sx={{ width: "40%" }}>{a.question}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{answerText(a) || "Open"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{citationText((a.citations || [])[0])}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Management sign-off</Typography>
            <Stack spacing={0.75}>
              {r.slots.map((s) => (
                <Stack key={s.key} direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ width: 170, fontWeight: 600 }}>{s.label}</Typography>
                  {s.unused ? <Chip size="small" label="Not used at this company" /> : s.signature ? (
                    <>
                      <Chip size="small" label={s.signature.decision} color={s.signature.decision === "ACCEPT" ? "success" : s.signature.decision === "HOLD" ? "warning" : "error"} />
                      <Typography variant="body2">{s.signature.name}, {new Date(s.signature.signedAt).toLocaleString()}</Typography>
                      {s.signature.comment && <Typography variant="body2" color="text.secondary">"{s.signature.comment}"</Typography>}
                    </>
                  ) : <Typography variant="body2" color="text.secondary">Not signed</Typography>}
                </Stack>
              ))}
            </Stack>
          </Card>
        </>
      )}
      <Typography variant="caption" color="text.secondary">
        This view states what the contract documents say and do not say. Every decision rests with the signatories and the Engineer of Record.
      </Typography>
    </Box>
  );
}
